import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useContext, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import { FileUploadContext } from '../../app/components/file-upload-modal/file-upload-context';
import { GlobalContext } from '../../app/context/global-context';
import { useTenant } from '../../app/context/tenant-context';
import { getTenantConfig, getTenantEnvironment, getTenants } from '../../app/services/tenants';
import { CR_USER_PROP, TokenUtil } from '../../app/utils/token';
import { AppContext } from '../context/app-context';
import { useTabManager } from '../context/tab-manager-context';

import { Tenant } from '@controlrooms/models';

export const useTenants = () => {
  const { user } = useAuth0();
  const { tenant: currentTenantId } = useTenant();
  const { demoAccessToken } = useContext(GlobalContext);
  let userId: number | undefined;

  userId = user?.[CR_USER_PROP]?.user_id;

  if (demoAccessToken) {
    const extracted = TokenUtil.extractUserProps(demoAccessToken);
    userId = extracted?.user_id;
  }

  const key = ['my-tenants'];
  const { data: tenants, ...rest } = useQuery(key, () => getTenants(), {
    enabled: Boolean(userId),
    select: ({ result }) =>
      result.map((t) => ({ ...t, selected: t.id === currentTenantId })).sort((a, b) => a.id - b.id),
    cacheTime: 8 * 60 * 60 * 1000, // 8 hours,
    staleTime: 60 * 60 * 1000, // 1 hour,
  });

  const currentTenant = useMemo(() => {
    const currentTenant = tenants?.find((t) => t.id === currentTenantId);
    if (tenants?.length && currentTenantId && !currentTenant)
      throw new Error('You may not have the access to view this tenant!');
    return currentTenant;
  }, [tenants, currentTenantId]) as Tenant;

  return {
    currentTenant,
    tenants,
    ...rest,
  };
};

export const useTenantEnvironment = (tenantId: number | undefined) => {
  const { tenants } = useTenants();
  const environment = tenants?.find((t) => t.id === tenantId)?.environmentId;
  const key = [
    'tenant-environment',
    {
      tenant: tenantId,
      environment,
    },
  ];

  if (tenantId && tenants && !environment) throw new Error('Invalid tenant environment');

  return useQuery(key, () => getTenantEnvironment(), {
    enabled: environment !== undefined,
    select: ({ result }) => result,
    cacheTime: 8 * 60 * 60 * 1000, // 8 hours,
    staleTime: 60 * 60 * 1000, // 1 hour,
  });
};

export const useSwitchTenants = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { tenant: currentTenant, updateTenant } = useTenant();
  const queryClient = useQueryClient();
  const [, setSearchParams] = useSearchParams();
  const { resetFilesAndProgress } = useContext(FileUploadContext);
  const { setTenantSwitching } = useContext(AppContext);
  // Get handleTenantSwitch from TabManagerContext to clear tabs when tenant changes
  // This is safe because TenantSelector is under TabManagerProvider hierarchy
  // Use try-catch to handle cases where TabManagerProvider might not be available (LayoutV2)
  let handleTenantSwitch: (() => void) | undefined;
  try {
    const tabManager = useTabManager();
    handleTenantSwitch = tabManager.handleTenantSwitch;
  } catch {
    // TabManagerProvider not available (e.g., in LayoutV2) - this is expected
    handleTenantSwitch = undefined;
  }

  const switchTenant = useCallback(
    async (tenantId: number, params?: URLSearchParams) => {
      // Early return if switching to same tenant with just params change
      if (currentTenant === tenantId) {
        if (params) setSearchParams(params);
        return;
      }

      setTenantSwitching(true);

      try {
        // Clear tabs before switching tenant (only if TabManagerProvider is available)
        // In LayoutV3, this will clear all tabs when tenant changes
        handleTenantSwitch?.();

        // Parallel: Fetch token + reset file state
        await Promise.all([
          getAccessTokenSilently({
            cacheMode: 'off',
            authorizationParams: { tenant: tenantId },
          }),
          Promise.resolve(resetFilesAndProgress()),
        ]);

        // Clear session storage
        sessionStorage.clear();

        // Targeted cache invalidation - remove only queries for previous tenant
        queryClient.removeQueries({
          predicate: (query) => {
            const key = query.queryKey;
            // Remove queries that have tenant-specific data in their key
            return (
              Array.isArray(key) &&
              key.some(
                (k) =>
                  typeof k === 'object' &&
                  k !== null &&
                  'tenant' in k &&
                  k.tenant === currentTenant,
              )
            );
          },
        });

        // Navigate to new tenant
        updateTenant(tenantId, params);

        // Prefetch critical data for new tenant
        await queryClient.prefetchQuery(['my-tenants'], getTenants);

        setTenantSwitching(false);
      } catch (error) {
        console.error('Tenant switch failed:', error);
        setTenantSwitching(false);
        // Re-throw to allow caller to handle if needed
        throw error;
      }
    },
    [
      currentTenant,
      setSearchParams,
      setTenantSwitching,
      handleTenantSwitch,
      getAccessTokenSilently,
      resetFilesAndProgress,
      queryClient,
      updateTenant,
    ],
  );
  return { switchTenant };
};

export const useGetTenantConfig = () => {
  const key = ['tenant_config'];
  return useQuery(key, () => getTenantConfig(), {
    cacheTime: 8 * 60 * 60 * 1000, // 8 hours,
    staleTime: 8 * 60 * 60 * 1000, // 8 hours,
  });
};
